import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';

interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  query: string;
  parameters?: any[];
}

interface QueryResult {
  columns: string[];
  rows: any[];
  executionTime: number;
  rowCount: number;
  queryType?: string;
  status?: string;
  error?: string;
}

const Reports: React.FC = () => {
  const [activeTab, setActiveTab] = useState('templates');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queryResult, setQueryResult] = useState<QueryResult | null>(null);
  const [customQuery, setCustomQuery] = useState('');
  const [queryType, setQueryType] = useState('auto');
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null);
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [devices, setDevices] = useState<any[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [queryScope, setQueryScope] = useState<'all' | 'selected'>('all');
  const [deviceSearch, setDeviceSearch] = useState('');

  // Fallback report templates (defined here so loadTemplates can access it)
  // Full definition is below after useEffect
  let reportTemplates: ReportTemplate[] = [];

  // Load templates from API
  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      console.log('Loading templates from API...');
      const data = await apiService.getQueryTemplates();
      console.log('Templates loaded:', data);
      setTemplates(data.templates || []);
    } catch (err) {
      console.error('Error loading templates:', err);
      // Fallback to hardcoded templates
      console.log('Using fallback templates');
      // reportTemplates will be defined before this runs
      if (reportTemplates.length > 0) {
        setTemplates(reportTemplates);
      }
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  // Load devices for selection
  const loadDevices = async () => {
    try {
      console.log('Loading devices for selection...');
      const devicesData = await apiService.getDevices();
      console.log('Devices loaded:', devicesData);
      setDevices(devicesData);
    } catch (err) {
      console.error('Error loading devices:', err);
      // Set some fallback devices for testing
      setDevices([
        { device_id: 'test-1', name: 'DESKTOP-OAQ304D', os: 'windows', status: 'active' },
        { device_id: 'test-2', name: 'DESKTOP-FIN-001', os: 'windows', status: 'active' },
        { device_id: 'test-3', name: 'DESKTOP-FIN-002', os: 'windows', status: 'active' }
      ]);
    }
  };

  // Filter devices based on search
  const filteredDevices = devices.filter(device => 
    device.name.toLowerCase().includes(deviceSearch.toLowerCase()) ||
    device.os.toLowerCase().includes(deviceSearch.toLowerCase()) ||
    device.status.toLowerCase().includes(deviceSearch.toLowerCase())
  );

  // Load templates and devices on component mount
  useEffect(() => {
    loadTemplates();
    loadDevices();
  }, [loadTemplates]);

  // Fallback report templates (in case API fails) - assign to variable declared above
  reportTemplates = [
    {
      id: 'policy-compliance',
      name: 'Policy Compliance Report',
      description: 'Detailed analysis of policy compliance across all devices',
      category: 'Policy',
      query: `
        SELECT 
          d.name as device_name,
          d.os,
          d.status,
          d.compliance_status,
          p.name as policy_name,
          p.status as policy_status,
          pd.deployment_status,
          pd.last_check,
          CASE 
            WHEN pd.deployment_status = 'deployed' AND d.compliance_status = 'compliant' THEN 'Compliant'
            WHEN pd.deployment_status = 'deployed' AND d.compliance_status = 'non_compliant' THEN 'Non-Compliant'
            WHEN pd.deployment_status = 'pending' THEN 'Pending'
            ELSE 'Unknown'
          END as compliance_result
        FROM devices d
        LEFT JOIN policy_deployments pd ON d.device_id = pd.device_id
        LEFT JOIN policies p ON pd.policy_id = p.id
        ORDER BY d.name, p.name
      `
    },
    {
      id: 'device-inventory',
      name: 'Device Inventory Report',
      description: 'Complete hardware and software inventory with details',
      category: 'Inventory',
      query: `
        SELECT 
          d.name as device_name,
          d.os,
          d.platform,
          d.status,
          d.last_seen,
          d.device_metadata->>'cpu' as cpu_info,
          d.device_metadata->>'memory' as memory_info,
          d.device_metadata->>'disk_space' as disk_space,
          d.device_metadata->>'installed_software' as software_list
        FROM devices d
        ORDER BY d.name
      `
    },
    {
      id: 'security-assessment',
      name: 'Security Assessment Report',
      description: 'Security status and vulnerability assessment report',
      category: 'Security',
      query: `
        SELECT 
          d.name as device_name,
          d.os,
          d.compliance_status,
          d.device_metadata->>'antivirus_status' as antivirus_status,
          d.device_metadata->>'firewall_status' as firewall_status,
          d.device_metadata->>'encryption_status' as encryption_status,
          d.device_metadata->>'last_security_scan' as last_security_scan,
          CASE 
            WHEN d.compliance_status = 'compliant' AND 
                 (d.device_metadata->>'antivirus_status' = 'enabled' OR d.device_metadata->>'antivirus_status' = 'active') AND
                 (d.device_metadata->>'firewall_status' = 'enabled' OR d.device_metadata->>'firewall_status' = 'active')
            THEN 'Secure'
            ELSE 'Needs Attention'
          END as security_status
        FROM devices d
        ORDER BY security_status, d.name
      `
    },
    {
      id: 'performance-analysis',
      name: 'Performance Analysis Report',
      description: 'Device performance metrics and resource utilization',
      category: 'Performance',
      query: `
        SELECT 
          d.name as device_name,
          d.os,
          d.status,
          d.device_metadata->>'cpu_usage' as cpu_usage,
          d.device_metadata->>'memory_usage' as memory_usage,
          d.device_metadata->>'disk_usage' as disk_usage,
          d.device_metadata->>'network_usage' as network_usage,
          d.last_seen,
          CASE 
            WHEN CAST(d.device_metadata->>'cpu_usage' AS FLOAT) > 80 OR 
                 CAST(d.device_metadata->>'memory_usage' AS FLOAT) > 80 OR
                 CAST(d.device_metadata->>'disk_usage' AS FLOAT) > 90
            THEN 'High Usage'
            WHEN CAST(d.device_metadata->>'cpu_usage' AS FLOAT) > 60 OR 
                 CAST(d.device_metadata->>'memory_usage' AS FLOAT) > 60 OR
                 CAST(d.device_metadata->>'disk_usage' AS FLOAT) > 75
            THEN 'Moderate Usage'
            ELSE 'Normal'
          END as performance_status
        FROM devices d
        WHERE d.device_metadata IS NOT NULL
        ORDER BY performance_status, d.name
      `
    }
  ];

  const executeQuery = async (query: string, type: string = 'auto', targetDevices?: string[]) => {
    setLoading(true);
    setError(null);
    setQueryResult(null);

    try {
      console.log('Executing query:', { query, type, targetDevices });
      
      // Modify query to include device filtering if specific devices are selected
      let modifiedQuery = query;
      if (targetDevices && targetDevices.length > 0) {
        if (type === 'sql' && query.toLowerCase().includes('from devices')) {
          const deviceIds = targetDevices.map(id => `'${id}'`).join(',');
          // Check if WHERE clause already exists
          if (query.toLowerCase().includes('where')) {
            // Add device_id filter to existing WHERE clause
            modifiedQuery = query.replace(/WHERE/i, `WHERE device_id IN (${deviceIds}) AND`);
          } else {
            // Add new WHERE clause
            modifiedQuery = query.replace(/FROM devices/i, `FROM devices WHERE device_id IN (${deviceIds})`);
          }
        }
      }
      
      // Call backend endpoint with query type and device context
      const result = await apiService.executeCustomQuery(modifiedQuery, type);
      console.log('Query result:', result);
      
      // Add hostname information to results if not already present
      if (result.rows && result.rows.length > 0) {
        const enhancedRows = await Promise.all(result.rows.map(async (row: any) => {
          // Add hostname if not present
          if (!row.hostname && !row.device_name && !row.name) {
            // Try to find device info from our devices list
            const deviceInfo = devices.find(d => d.device_id === row.device_id);
            if (deviceInfo) {
              row.hostname = deviceInfo.name;
              row.device_name = deviceInfo.name;
            }
          }
          
          // If this is a device row, try to get detailed information
          if (row.device_id && (row.cpu_info === '-' || row.memory_info === '-' || row.disk_space === '-')) {
            try {
              const deviceDetails = await apiService.getDeviceDetails(row.device_id);
              row.cpu_info = `${deviceDetails.cpu_info.cpu_count} cores, ${deviceDetails.cpu_info.cpu_percent} usage`;
              row.memory_info = `${deviceDetails.memory_info.total} total, ${deviceDetails.memory_info.usage_percent} used`;
              row.disk_space = `${deviceDetails.disk_space.total} total, ${deviceDetails.disk_space.usage_percent} used`;
              row.software_list = `${deviceDetails.process_count} processes running`;
            } catch (err) {
              console.log('Could not get detailed device info:', err);
            }
          }
          
          return row;
        }));
        result.rows = enhancedRows;
      }
      
      setQueryResult(result);
    } catch (err) {
      console.error('Query execution error:', err);
      setError(err instanceof Error ? err.message : 'Failed to execute query');
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async (template: ReportTemplate) => {
    setSelectedTemplate(template);
    const targetDevices = queryScope === 'selected' ? selectedDevices : undefined;
    await executeQuery(template.query, 'sql', targetDevices);
  };

  const exportToExcel = async (data: QueryResult, filename: string) => {
    try {
      // This would call a backend endpoint to generate Excel file
      const response = await apiService.exportToExcel(data, filename);
      
      // Create download link
      const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export to Excel');
    }
  };

  const TabButton: React.FC<{ id: string; label: string; icon: string; active: boolean }> = ({ id, label, icon, active }) => (
    <button
      className={`tab-button ${active ? 'active' : ''}`}
      onClick={() => setActiveTab(id)}
    >
      <span className="tab-icon">{icon}</span>
      <span className="tab-label">{label}</span>
    </button>
  );

  const TemplatesTab = () => (
    <div>
      <div className="card">
        <h2 className="card-title">Report Templates</h2>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>
          Pre-built report templates for common analysis needs
        </p>
        
        {/* Device Selection */}
        <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>Query Scope</h3>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input 
                type="radio" 
                name="queryScope" 
                value="all" 
                checked={queryScope === 'all'}
                onChange={(e) => setQueryScope(e.target.value as 'all' | 'selected')}
              />
              All Devices
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input 
                type="radio" 
                name="queryScope" 
                value="selected" 
                checked={queryScope === 'selected'}
                onChange={(e) => setQueryScope(e.target.value as 'all' | 'selected')}
              />
              Selected Devices
            </label>
          </div>
          
          {queryScope === 'selected' && (
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Select Devices:
              </label>
              
              {/* Search Box */}
              <div style={{ marginBottom: '12px' }}>
                <input
                  type="text"
                  placeholder="Search devices by name, OS, or status..."
                  value={deviceSearch}
                  onChange={(e) => setDeviceSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              {/* Select All/None Buttons */}
              <div style={{ marginBottom: '8px', display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setSelectedDevices(filteredDevices.map(d => d.device_id))}
                  style={{
                    padding: '4px 8px',
                    fontSize: '12px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedDevices([])}
                  style={{
                    padding: '4px 8px',
                    fontSize: '12px',
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Clear All
                </button>
              </div>
              
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #d1d5db', borderRadius: '6px', padding: '8px' }}>
                {filteredDevices.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                    {deviceSearch ? 'No devices found matching your search' : 'No devices available'}
                  </div>
                ) : (
                  filteredDevices.map((device) => (
                    <label key={device.device_id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
                      <input 
                        type="checkbox" 
                        checked={selectedDevices.includes(device.device_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedDevices([...selectedDevices, device.device_id]);
                          } else {
                            setSelectedDevices(selectedDevices.filter(id => id !== device.device_id));
                          }
                        }}
                      />
                      <span style={{ fontSize: '14px' }}>
                        <strong>{device.name}</strong> ({device.os}) - {device.status}
                        {device.compliance_status && (
                          <span style={{ 
                            marginLeft: '8px', 
                            padding: '2px 6px', 
                            borderRadius: '3px', 
                            fontSize: '12px',
                            backgroundColor: device.compliance_status === 'compliant' ? '#dcfce7' : '#fef3c7',
                            color: device.compliance_status === 'compliant' ? '#166534' : '#92400e'
                          }}>
                            {device.compliance_status}
                          </span>
                        )}
                      </span>
                    </label>
                  ))
                )}
              </div>
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                Showing: {filteredDevices.length} of {devices.length} devices | Selected: {selectedDevices.length} device(s)
              </div>
            </div>
          )}
        </div>
        
        {templatesLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
            <p style={{ color: '#6b7280' }}>Loading templates...</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px' }}>
            {templates.map((template) => (
            <div key={template.id} style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', backgroundColor: 'white' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                  {template.name}
                </h3>
                <span style={{ 
                  padding: '4px 8px', 
                  borderRadius: '6px', 
                  fontSize: '12px', 
                  fontWeight: '500',
                  backgroundColor: template.category === 'Policy' ? '#dbeafe' : 
                                 template.category === 'Inventory' ? '#dcfce7' :
                                 template.category === 'Security' ? '#fef3c7' : '#e0e7ff',
                  color: template.category === 'Policy' ? '#1e40af' : 
                        template.category === 'Inventory' ? '#166534' :
                        template.category === 'Security' ? '#92400e' : '#3730a3'
                }}>
                  {template.category}
                </span>
              </div>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 16px 0', lineHeight: '1.5' }}>
                {template.description}
              </p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  className="quick-action-btn primary"
                  onClick={() => generateReport(template)}
                  disabled={loading}
                >
                  {loading && selectedTemplate?.id === template.id ? 'Generating...' : 'Generate Report'}
                </button>
                <button 
                  className="quick-action-btn secondary"
                  onClick={() => setCustomQuery(template.query)}
                >
                  View Query
                </button>
              </div>
            </div>
          ))}
          </div>
        )}
        
        {/* Inline Results Display */}
        {queryResult && (
          <div style={{ marginTop: '24px' }}>
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 className="card-title">Query Results</h2>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    className="quick-action-btn secondary"
                    onClick={() => exportToExcel(queryResult, `report_${Date.now()}`)}
                  >
                    Export to Excel
                  </button>
                  <button 
                    className="quick-action-btn secondary"
                    onClick={() => {
                      const csv = [queryResult.columns.join(','), ...queryResult.rows.map(row => Object.values(row).join(','))].join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `report_${Date.now()}.csv`;
                      link.click();
                      window.URL.revokeObjectURL(url);
                    }}
                  >
                    Export to CSV
                  </button>
                </div>
              </div>

              {/* Results Stats */}
              <div style={{ 
                display: 'flex', 
                gap: '20px', 
                marginBottom: '20px', 
                padding: '12px', 
                backgroundColor: '#f9fafb', 
                borderRadius: '8px' 
              }}>
                <div>
                  <span style={{ fontWeight: '500', color: '#374151' }}>Rows:</span> {queryResult.rowCount}
                </div>
                <div>
                  <span style={{ fontWeight: '500', color: '#374151' }}>Execution Time:</span> {queryResult.executionTime}ms
                </div>
                <div>
                  <span style={{ fontWeight: '500', color: '#374151' }}>Columns:</span> {queryResult.columns.length}
                </div>
                {queryResult.queryType && (
                  <div>
                    <span style={{ fontWeight: '500', color: '#374151' }}>Type:</span> {queryResult.queryType}
                  </div>
                )}
                {queryResult.status && (
                  <div>
                    <span style={{ fontWeight: '500', color: '#374151' }}>Status:</span> 
                    <span style={{ 
                      color: queryResult.status === 'success' ? '#059669' : '#dc2626',
                      marginLeft: '4px'
                    }}>
                      {queryResult.status}
                    </span>
                  </div>
                )}
              </div>

              {/* Results Table */}
              <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb' }}>
                      {queryResult.columns.map((column, index) => (
                        <th key={index} style={{ 
                          padding: '12px', 
                          textAlign: 'left', 
                          fontWeight: '600', 
                          color: '#374151',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {queryResult.rows.map((row, rowIndex) => (
                      <tr key={rowIndex} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        {queryResult.columns.map((column, colIndex) => (
                          <td key={colIndex} style={{ 
                            padding: '12px', 
                            color: '#374151',
                            fontSize: '14px'
                          }}>
                            {row[column] || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const CustomQueryTab = () => (
    <div>
      <div className="card">
        <h2 className="card-title">Custom Query Builder</h2>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>
          Execute custom SQL queries and OSquery commands
        </p>
        
        {/* Device Selection for Custom Query */}
        <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>Query Scope</h3>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input 
                type="radio" 
                name="customQueryScope" 
                value="all" 
                checked={queryScope === 'all'}
                onChange={(e) => setQueryScope(e.target.value as 'all' | 'selected')}
              />
              All Devices
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input 
                type="radio" 
                name="customQueryScope" 
                value="selected" 
                checked={queryScope === 'selected'}
                onChange={(e) => setQueryScope(e.target.value as 'all' | 'selected')}
              />
              Selected Devices
            </label>
          </div>
          
          {queryScope === 'selected' && (
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Select Devices:
              </label>
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #d1d5db', borderRadius: '6px', padding: '8px' }}>
                {devices.map((device) => (
                  <label key={device.device_id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
                    <input 
                      type="checkbox" 
                      checked={selectedDevices.includes(device.device_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDevices([...selectedDevices, device.device_id]);
                        } else {
                          setSelectedDevices(selectedDevices.filter(id => id !== device.device_id));
                        }
                      }}
                    />
                    <span style={{ fontSize: '14px' }}>
                      {device.name} ({device.os}) - {device.status}
                    </span>
                  </label>
                ))}
              </div>
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                Selected: {selectedDevices.length} device(s)
              </div>
            </div>
          )}
        </div>
        
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
            Query Type
          </label>
          <select 
            value={queryType}
            onChange={(e) => setQueryType(e.target.value)}
            style={{ 
              width: '100%', 
              padding: '8px 12px', 
              border: '1px solid #d1d5db', 
              borderRadius: '6px',
              fontSize: '14px'
            }}
          >
            <option value="auto">Auto-detect</option>
            <option value="sql">SQL Query</option>
            <option value="osquery">OSquery</option>
            <option value="registry">Windows Registry</option>
            <option value="powershell">PowerShell</option>
          </select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
            Query
          </label>
          <textarea
            value={customQuery}
            onChange={(e) => setCustomQuery(e.target.value)}
            placeholder="Enter your query here... Examples:
SQL: SELECT * FROM devices WHERE os = 'windows'
OSquery: SELECT * FROM processes WHERE cpu_percent > 10
Registry: Check USB registry settings
PowerShell: Get-Process | Where-Object {$_.CPU -gt 10}"
            style={{
              width: '100%',
              height: '200px',
              padding: '12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'monospace',
              resize: 'vertical'
            }}
          />
        </div>

        {/* Sample Queries */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
            Sample Queries
          </label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button 
              className="quick-action-btn secondary"
              style={{ fontSize: '12px', padding: '4px 8px' }}
              onClick={() => {
                setCustomQuery("SELECT device_id, name as hostname, os, status, compliance_status FROM devices WHERE os = 'windows'");
                setQueryType('sql');
              }}
            >
              Windows Devices
            </button>
            <button 
              className="quick-action-btn secondary"
              style={{ fontSize: '12px', padding: '4px 8px' }}
              onClick={() => {
                setCustomQuery("SELECT pid, name, cpu_percent, memory_percent FROM processes WHERE cpu_percent > 10");
                setQueryType('osquery');
              }}
            >
              High CPU Processes
            </button>
            <button 
              className="quick-action-btn secondary"
              style={{ fontSize: '12px', padding: '4px 8px' }}
              onClick={() => {
                setCustomQuery("Check USB registry settings");
                setQueryType('registry');
              }}
            >
              USB Registry Check
            </button>
            <button 
              className="quick-action-btn secondary"
              style={{ fontSize: '12px', padding: '4px 8px' }}
              onClick={() => {
                setCustomQuery("Get-Process | Where-Object {$_.CPU -gt 10}");
                setQueryType('powershell');
              }}
            >
              PowerShell Processes
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button 
            className="quick-action-btn primary"
            onClick={() => {
              const targetDevices = queryScope === 'selected' ? selectedDevices : undefined;
              executeQuery(customQuery, queryType, targetDevices);
            }}
            disabled={loading || !customQuery.trim()}
          >
            {loading ? 'Executing...' : 'Execute Query'}
          </button>
          <button 
            className="quick-action-btn secondary"
            onClick={() => {
              setCustomQuery('');
              setQueryType('auto');
            }}
          >
            Clear
          </button>
          <button 
            className="quick-action-btn secondary"
            onClick={() => {
              setCustomQuery("SELECT device_id, name as hostname, os, status FROM devices LIMIT 5");
              setQueryType('sql');
            }}
          >
            Test Query
          </button>
        </div>
        
        {/* Inline Results Display for Custom Query */}
        {queryResult && (
          <div style={{ marginTop: '24px' }}>
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 className="card-title">Query Results</h2>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    className="quick-action-btn secondary"
                    onClick={() => exportToExcel(queryResult, `report_${Date.now()}`)}
                  >
                    Export to Excel
                  </button>
                  <button 
                    className="quick-action-btn secondary"
                    onClick={() => {
                      const csv = [queryResult.columns.join(','), ...queryResult.rows.map(row => Object.values(row).join(','))].join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `report_${Date.now()}.csv`;
                      link.click();
                      window.URL.revokeObjectURL(url);
                    }}
                  >
                    Export to CSV
                  </button>
                </div>
              </div>

              {/* Results Stats */}
              <div style={{ 
                display: 'flex', 
                gap: '20px', 
                marginBottom: '20px', 
                padding: '12px', 
                backgroundColor: '#f9fafb', 
                borderRadius: '8px' 
              }}>
                <div>
                  <span style={{ fontWeight: '500', color: '#374151' }}>Rows:</span> {queryResult.rowCount}
                </div>
                <div>
                  <span style={{ fontWeight: '500', color: '#374151' }}>Execution Time:</span> {queryResult.executionTime}ms
                </div>
                <div>
                  <span style={{ fontWeight: '500', color: '#374151' }}>Columns:</span> {queryResult.columns.length}
                </div>
                {queryResult.queryType && (
                  <div>
                    <span style={{ fontWeight: '500', color: '#374151' }}>Type:</span> {queryResult.queryType}
                  </div>
                )}
                {queryResult.status && (
                  <div>
                    <span style={{ fontWeight: '500', color: '#374151' }}>Status:</span> 
                    <span style={{ 
                      color: queryResult.status === 'success' ? '#059669' : '#dc2626',
                      marginLeft: '4px'
                    }}>
                      {queryResult.status}
                    </span>
                  </div>
                )}
              </div>

              {/* Results Table */}
              <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb' }}>
                      {queryResult.columns.map((column, index) => (
                        <th key={index} style={{ 
                          padding: '12px', 
                          textAlign: 'left', 
                          fontWeight: '600', 
                          color: '#374151',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {queryResult.rows.map((row, rowIndex) => (
                      <tr key={rowIndex} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        {queryResult.columns.map((column, colIndex) => (
                          <td key={colIndex} style={{ 
                            padding: '12px', 
                            color: '#374151',
                            fontSize: '14px'
                          }}>
                            {row[column] || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const ResultsTab = () => (
    <div>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 className="card-title">Query Results</h2>
          {queryResult && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button 
                className="quick-action-btn secondary"
                onClick={() => exportToExcel(queryResult, `report_${Date.now()}`)}
              >
                Export to Excel
              </button>
              <button 
                className="quick-action-btn secondary"
                onClick={() => {
                  const csv = [queryResult.columns.join(','), ...queryResult.rows.map(row => Object.values(row).join(','))].join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `report_${Date.now()}.csv`;
                  link.click();
                  window.URL.revokeObjectURL(url);
                }}
              >
                Export to CSV
              </button>
            </div>
          )}
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
            <p style={{ color: '#6b7280' }}>Executing query...</p>
          </div>
        )}

        {(error || (queryResult && queryResult.error)) && (
          <div style={{ 
            padding: '16px', 
            backgroundColor: '#fef2f2', 
            border: '1px solid #fecaca', 
            borderRadius: '8px',
            color: '#dc2626',
            marginBottom: '20px'
          }}>
            <strong>Error:</strong> {error || queryResult?.error}
          </div>
        )}

        {queryResult && (
          <div>
            <div style={{ 
              display: 'flex', 
              gap: '20px', 
              marginBottom: '20px', 
              padding: '12px', 
              backgroundColor: '#f9fafb', 
              borderRadius: '8px' 
            }}>
              <div>
                <span style={{ fontWeight: '500', color: '#374151' }}>Rows:</span> {queryResult.rowCount}
              </div>
              <div>
                <span style={{ fontWeight: '500', color: '#374151' }}>Execution Time:</span> {queryResult.executionTime}ms
              </div>
              <div>
                <span style={{ fontWeight: '500', color: '#374151' }}>Columns:</span> {queryResult.columns.length}
              </div>
              {queryResult.queryType && (
                <div>
                  <span style={{ fontWeight: '500', color: '#374151' }}>Type:</span> {queryResult.queryType}
                </div>
              )}
              {queryResult.status && (
                <div>
                  <span style={{ fontWeight: '500', color: '#374151' }}>Status:</span> 
                  <span style={{ 
                    color: queryResult.status === 'success' ? '#059669' : '#dc2626',
                    marginLeft: '4px'
                  }}>
                    {queryResult.status}
                  </span>
                </div>
              )}
            </div>

            <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    {queryResult.columns.map((column, index) => (
                      <th key={index} style={{ 
                        padding: '12px', 
                        textAlign: 'left', 
                        fontWeight: '600', 
                        color: '#374151',
                        borderBottom: '1px solid #e5e7eb'
                      }}>
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {queryResult.rows.map((row, rowIndex) => (
                    <tr key={rowIndex} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      {queryResult.columns.map((column, colIndex) => (
                        <td key={colIndex} style={{ 
                          padding: '12px', 
                          color: '#374151',
                          fontSize: '14px'
                        }}>
                          {row[column] || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!queryResult && !loading && !error && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            <svg width="48" height="48" fill="currentColor" viewBox="0 0 24 24" style={{ margin: '0 auto 16px', opacity: 0.5 }}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
            </svg>
            <p>No query results yet. Execute a query to see results here.</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div>
      {/* Tab Navigation */}
      <div className="tab-navigation">
        <TabButton id="templates" label="Templates" icon="📋" active={activeTab === 'templates'} />
        <TabButton id="custom" label="Custom Query" icon="🔍" active={activeTab === 'custom'} />
        <TabButton id="results" label="Results" icon="📊" active={activeTab === 'results'} />
      </div>

      {/* Tab Content */}
      {activeTab === 'templates' && <TemplatesTab />}
      {activeTab === 'custom' && <CustomQueryTab />}
      {activeTab === 'results' && <ResultsTab />}
    </div>
  );
};

export default Reports;
